import { AdminHeader } from "@/components/AdminHeader";
import { useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Download, Eye, FileImage, FileSignature, Search, CalendarRange, Clock, Printer, Mail } from "lucide-react";

const ROW = "border-[#E5DED5] hover:bg-[#F5EFE6]/50 transition-colors";

const DOC_LABELS = { license_front: "License Front", license_back: "License Back", passport: "Passport" };
const docLabel = (t) => DOC_LABELS[t] || (t || "Document").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const fmtDate = (d) => (d ? new Date(d + (d.length === 10 ? "T12:00:00" : "")).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

export default function ContractsTab() {
  const [contracts, setContracts] = useState(null);
  const [health, setHealth] = useState(null);
  const [pending, setPending] = useState(null);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = () => {
    api.get("/admin/contracts").then((r) => setContracts(r.data)).catch(() => setContracts([]));
  };
  useEffect(() => { load(); api.get("/admin/contracts/health").then((r) => setHealth(r.data)).catch(() => {}); api.get("/admin/contracts/pending-resign").then((r) => setPending(r.data)).catch(() => {}); }, []);

  const extendHours = async (b) => {
    const h = window.prompt(`Extend ${b.booking_ref} by how many HOURS? ($25/hr, 1-11, collected at return):`, "2");
    if (!h) return;
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/extend-hours`, { hours: parseInt(h, 10) });
      toast.success(`Extended ${data.hours} hr — $${Number(data.amount).toFixed(2)} added to the booking. Guest notified.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Extension failed");
    }
  };

  const changeDates = async (b) => {
    const s = window.prompt(`Change dates for ${b.booking_ref} (${b.vehicle_name}) — new PICKUP date (YYYY-MM-DD):`, b.start_date);
    if (!s) return;
    const e = window.prompt("New RETURN date (YYYY-MM-DD):", b.end_date);
    if (!e) return;
    const pt = window.prompt("Pickup time (e.g. 10:00 AM):", b.pickup_time || "TBD");
    if (pt === null) return;
    const dt = window.prompt("Return time (e.g. 10:00 AM):", b.dropoff_time || "TBD");
    if (dt === null) return;
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/change-dates`, { start_date: s, end_date: e, pickup_time: pt, dropoff_time: dt });
      toast.success(`Updated → ${data.start_date} → ${data.end_date} · pickup ${data.pickup_time} / return ${data.dropoff_time} (${data.days} days${data.price_delta ? `, ${data.price_delta > 0 ? "+" : "−"}$${Math.abs(data.price_delta).toFixed(2)}` : ""}). Guest emailed/texted to re-sign; new dates blacked out.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Date change failed");
    }
  };

  const download = async (url, fallbackName) => {
    try {
      const r = await api.get(url, { responseType: "blob" });
      const m = (r.headers["content-disposition"] || "").match(/filename="?([^";]+)"?/);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = m ? m[1] : fallbackName;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const view = async (url) => {
    const w = window.open("", "_blank");
    try {
      const r = await api.get(url, { responseType: "blob" });
      w.location = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    } catch (e) {
      if (w) w.close();
      toast.error(errMsg(e));
    }
  };

  const filtered = useMemo(() => {
    let list = contracts || [];
    if (from) list = list.filter((b) => (b.start_date || "") >= from);
    if (to) list = list.filter((b) => (b.start_date || "") <= to);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter((b) => [b.booking_ref, b.customer_name, b.customer_email, b.vehicle_name, b.contract_signer].filter(Boolean).join(" ").toLowerCase().includes(s));
    }
    return list;
  }, [contracts, q, from, to]);

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const headers = ["Booking Ref", "Customer", "Email", "Vehicle", "Rental Start", "Rental End", "Status", "Signed By", "Signed At", "Re-signed At", "Documents"];
    const rows = filtered.map((b) => [
      b.booking_ref, b.customer_name, b.customer_email, b.vehicle_name, b.start_date, b.end_date, b.status,
      b.contract_signer || "", b.contract_signed_at || "", b.contract_re_signed_at || "",
      (b.documents || []).map((d) => docLabel(d.doc_type)).join("; "),
    ].map(esc).join(","));
    const csv = [headers.map(esc).join(","), ...rows].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `easydrive-contracts-${from || "all"}-to-${to || "all"}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${filtered.length} contract${filtered.length === 1 ? "" : "s"}`);
  };

  if (contracts === null) return <p className="text-muted-foreground font-ui">Loading contracts…</p>;

  return (
    <div data-testid="admin-contracts-tab">
      <AdminHeader
        testid="admin-header-contracts"
        eyebrow="Documents"
        title="Signed Contracts"
        description="Every signed rental agreement with the renter's license & passport photos — all downloadable for your records."
        actions={<>
          <input data-testid="contracts-date-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} title="Rental start from" className="px-3 py-2 rounded-full border border-[#E5DED5] bg-white text-sm font-ui focus:outline-none focus:ring-2 focus:ring-[#C71E2E]/30" />
          <span className="text-xs font-ui text-[#4A4A4A]">to</span>
          <input data-testid="contracts-date-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} title="Rental start to" className="px-3 py-2 rounded-full border border-[#E5DED5] bg-white text-sm font-ui focus:outline-none focus:ring-2 focus:ring-[#C71E2E]/30" />
          <Button data-testid="contracts-export-csv" size="sm" variant="outline" onClick={exportCsv} disabled={!filtered.length} className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] font-ui">
            <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
          </Button>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4A4A4A]" />
            <input data-testid="contracts-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ref, customer, vehicle…" className="pl-9 pr-4 py-2 rounded-full border border-[#E5DED5] bg-white text-sm font-ui w-60 focus:outline-none focus:ring-2 focus:ring-[#C71E2E]/30" />
          </div>
        </>}
      />
      {pending && pending.count > 0 && (
        <div data-testid="contracts-pending-resign" className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 p-5">
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-amber-800 flex items-center gap-2">
            <FileSignature size={14} /> Contracts renewed — {pending.count} guest{pending.count === 1 ? "" : "s"} still need{pending.count === 1 ? "s" : ""} to re-sign
          </p>
          <ul className="mt-3 space-y-2">
            {pending.bookings.map((b) => (
              <li key={b.id} data-testid={`pending-resign-${b.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-ui text-amber-900">
                <b>{b.booking_ref}</b> {b.customer_name} · {b.vehicle_name} · returns {fmtDate(b.end_date)}
                <span className="text-[10px] uppercase tracking-wide border border-amber-300 bg-white/60 rounded-full px-2 py-0.5">{b.reason === "extended" ? "rental extended" : "details changed"}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs font-ui text-amber-800/80">Each guest was emailed and texted a re-sign link automatically — this list clears as they sign.</p>
        </div>
      )}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <p data-testid="contracts-count" className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A]">{filtered.length} signed contract{filtered.length === 1 ? "" : "s"}</p>
        {pending && (
          <span data-testid="contracts-pending-count" className={`inline-flex items-center gap-1.5 text-[10px] font-ui font-semibold tracking-[0.12em] uppercase rounded-full px-2.5 py-1 border ${pending.count > 0 ? "text-amber-700 border-amber-300 bg-amber-50" : "text-emerald-700 border-emerald-300 bg-emerald-50"}`}>
            {pending.count} awaiting re-sign
          </span>
        )}
        {health && health.failures.length === 0 && (
          <span data-testid="contracts-health-ok" className="inline-flex items-center gap-1.5 text-[10px] font-ui font-semibold tracking-[0.12em] uppercase text-emerald-700 border border-emerald-300 bg-emerald-50 rounded-full px-2.5 py-1">
            PDF health: all {health.checked} contracts healthy
          </span>
        )}
        {health && health.failures.length > 0 && (
          <span data-testid="contracts-health-fail" className="inline-flex items-center gap-1.5 text-[10px] font-ui font-semibold tracking-[0.12em] uppercase text-red-700 border border-red-300 bg-red-50 rounded-full px-2.5 py-1" title={health.failures.map((f) => `${f.booking_ref}: ${f.error}`).join("\n")}>
            PDF health: {health.failures.length} of {health.checked} fail to build ({health.failures.map((f) => f.booking_ref).join(", ")})
          </span>
        )}
      </div>
      <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-[#E5DED5] hover:bg-transparent">
            <TableHead className="font-ui text-[11px] tracking-[0.15em] uppercase text-[#4A4A4A]">Ref</TableHead>
            <TableHead className="font-ui text-[11px] tracking-[0.15em] uppercase text-[#4A4A4A]">Customer</TableHead>
            <TableHead className="font-ui text-[11px] tracking-[0.15em] uppercase text-[#4A4A4A]">Vehicle</TableHead>
            <TableHead className="font-ui text-[11px] tracking-[0.15em] uppercase text-[#4A4A4A]">Rental Dates</TableHead>
            <TableHead className="font-ui text-[11px] tracking-[0.15em] uppercase text-[#4A4A4A]">Signed</TableHead>
            <TableHead className="font-ui text-[11px] tracking-[0.15em] uppercase text-[#4A4A4A]">Downloads</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 && (
            <TableRow className={ROW}><TableCell colSpan={6} className="text-center text-muted-foreground font-ui py-10">No signed contracts yet.</TableCell></TableRow>
          )}
          {filtered.map((b) => (
            <TableRow key={b.id} data-testid={`contract-row-${b.id}`} className={ROW}>
              <TableCell className="font-ui text-sm font-semibold text-[#C71E2E] whitespace-nowrap">{b.booking_ref}</TableCell>
              <TableCell className="text-sm">
                <div className="font-medium text-[#1A1A1A]">{b.customer_name}</div>
                <div className="text-xs text-muted-foreground">{b.customer_email}</div>
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm">{b.vehicle_name}</TableCell>
              <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{fmtDate(b.start_date)} → {fmtDate(b.end_date)}</TableCell>
              <TableCell className="text-sm">
                <div data-testid={`contract-signed-${b.id}`} className="font-medium text-emerald-700">{b.contract_signer || "—"} · {fmtTs(b.contract_signed_at)}</div>
                {b.contract_re_signed_at && (
                  <div data-testid={`contract-resigned-${b.id}`} className="mt-0.5 text-[10px] font-ui font-semibold tracking-[0.08em] uppercase text-emerald-700">Re-signed {fmtTs(b.contract_re_signed_at)}</div>
                )}
              </TableCell>
              <TableCell>
                <Button data-testid={`contract-view-${b.id}`} size="sm" variant="outline" onClick={() => view(`/admin/bookings/${b.id}/contract.pdf`)} title="View signed contract in browser" className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] font-ui mr-1.5 mb-1">
                  <Eye className="h-3.5 w-3.5 mr-1" /> View
                </Button>
                <Button data-testid={`contract-pdf-${b.id}`} size="sm" onClick={() => download(`/admin/bookings/${b.id}/contract.pdf`, `agreement-${b.booking_ref}.pdf`)} className="rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui mr-1.5 mb-1">
                  <Download className="h-3.5 w-3.5 mr-1" /> Contract PDF
                </Button>
                <Button data-testid={`contract-packet-${b.id}`} size="sm" variant="outline" onClick={() => download(`/admin/bookings/${b.id}/contract-packet.pdf`, `packet-${b.booking_ref}.pdf`)} title="Signed agreement + license/passport photos in one PDF" className="rounded-full border-[#C71E2E]/40 bg-transparent text-[#C71E2E] hover:bg-[#C71E2E]/5 font-ui mr-1.5 mb-1">
                  <Download className="h-3.5 w-3.5 mr-1" /> Packet
                </Button>
                {b.status !== "cancelled" && (
                  <Button data-testid={`contract-dates-${b.id}`} size="sm" variant="outline" onClick={() => changeDates(b)} title="Change the rental dates & times on this contract — vehicle stays blocked for the new dates, guest re-signs" className="rounded-full border-violet-300 bg-transparent text-violet-700 hover:bg-violet-50 font-ui mr-1.5 mb-1">
                    <CalendarRange className="h-3.5 w-3.5 mr-1" /> Dates
                  </Button>
                )}
                {["confirmed", "pending_payment"].includes(b.status) && (
                  <Button data-testid={`contract-hours-${b.id}`} size="sm" variant="outline" onClick={() => extendHours(b)} title="Extend the return by a few hours at $25/hr — collected at return, no contract re-sign" className="rounded-full border-amber-300 bg-transparent text-amber-700 hover:bg-amber-50 font-ui mr-1.5 mb-1">
                    <Clock className="h-3.5 w-3.5 mr-1" /> +Hours
                  </Button>
                )}
                <Button data-testid={`contract-print-${b.id}`} size="sm" variant="outline" onClick={() => view(`/admin/bookings/${b.id}/contract.pdf`)} title="Open the contract PDF in a new tab — print or save from there" className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] font-ui mr-1.5 mb-1">
                  <Printer className="h-3.5 w-3.5 mr-1" /> Print
                </Button>
                {(b.contract_resign_pending || !b.contract_signed_at) && b.status !== "cancelled" && (
                  <Button data-testid={`contract-resend-${b.id}`} size="sm" variant="outline" onClick={async () => { try { await api.post(`/admin/bookings/${b.id}/resend-contract`); toast.success(`Contract signing link re-sent to ${b.customer_name} (email${b.customer_phone ? " + SMS" : ""})`); } catch (err) { toast.error(errMsg(err)); } }} title="Re-email + re-text the contract signing link" className="rounded-full border-amber-300 bg-transparent text-amber-700 hover:bg-amber-50 font-ui mr-1.5 mb-1">
                    <Mail className="h-3.5 w-3.5 mr-1" /> Resend
                  </Button>
                )}
                {(b.documents || []).map((d) => (
                  <Button key={d.id} data-testid={`contract-doc-${d.id}`} size="sm" variant="outline" onClick={() => download(`/admin/documents/${d.id}/download`, `${d.doc_type}.jpg`)} className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] font-ui mr-1.5 mb-1 text-xs">
                    <FileImage className="h-3 w-3 mr-1" /> {docLabel(d.doc_type)}
                  </Button>
                ))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
    </div>
  );
}
